import { SharetribeApi } from './api';
import { EnhancedServiceTypes, ListingDeliveryType, ReferralStatus } from './enums';

/**
 * Updates the referral status on the referrer's profile after a new user signs up.
 *
 * @param {object} params - The parameters object.
 * @param {string} params.referrerId - The ID of the user who referred the current user.
 * @param {string} params.currentUserId - The ID of the newly signed-up user.
 * @param {ReferralStatus} [params.status=ReferralStatus.SIGNED_UP] - The referral status to set. Defaults to SIGNED_UP.
 * @returns {Promise<void>} A Promise that resolves when the update is complete.
 */
export const updateReferrerProfile = async ({
  referrerId,
  currentUserId,
  status = ReferralStatus.SIGNED_UP,
}) => {
  console.log(referrerId, currentUserId, status, 'updateReferrerProfile');
  // 1. Input Validation and Early Exit
  if (!referrerId) {
    return;
  }

  try {
    // 2. Await the asynchronous API call
    await SharetribeApi.updateReferrerProfile({
      id: referrerId,
      referredUserId: currentUserId,
      status: status,
    });
  } catch (error) {
    // throw error;
  }
};

// Helper function to convert image URL to File object
export async function urlToFile(url, filename) {
  try {
    const response = await fetch(url, {
      mode: 'cors', // Add CORS mode if needed
      headers: {
        Accept: 'image/*',
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch image: ${response.status} ${response.statusText}`);
    }

    const blob = await response.blob();

    // Validate that we got a valid image blob
    if (!blob.type.startsWith('image/')) {
      throw new Error(`Invalid content type: ${blob.type}`);
    }

    return new File([blob], filename, { type: blob.type });
  } catch (error) {
    throw error;
  }
}

export const setListingSessionUrl = path => {
  if (typeof window !== 'undefined') {
    window.sessionStorage.setItem('listingSessionUrl', path);
  }
};

export const getListingSessionUrl = () => {
  if (typeof window !== 'undefined') {
    return window.sessionStorage.getItem('listingSessionUrl');
  }
};

export const deleteListingSessionUrl = () => {
  if (typeof window !== 'undefined') {
    window.sessionStorage.removeItem('listingSessionUrl');
  }
};

const DELIVERY_MAP = {
  autographs: ListingDeliveryType.PHYSICAL_ITEM,
  shoutoutsOrGreetings: ListingDeliveryType.DIGITAL_DELIVERY,
  socialMedia: ListingDeliveryType.DIGITAL_DELIVERY,
  creativeMedia: ListingDeliveryType.DIGITAL_DELIVERY,
  appearances: ListingDeliveryType.PHYSICAL_ITEM,
  mentorship: ListingDeliveryType.PHYSICAL_ITEM,
  businessDevelopment: ListingDeliveryType.PHYSICAL_ITEM,
};

export const getListingDeliveryType = (type, title) => {
  // Handle the exception
  if (type === EnhancedServiceTypes.EXCLUSIVE_TRAINING) {
    return title === 'Virtual Training Camp'
      ? ListingDeliveryType.LIVE_SESSION
      : ListingDeliveryType.PHYSICAL_ITEM;
  }

  // Return from map or default to empty string
  return DELIVERY_MAP[type] || '';
};

// Get delivery type label
export const getDeliveryLabel = deliveryType => {
  switch (deliveryType) {
    case 'physicalItem':
      return 'Physical Item';
    case 'digitalDelivery':
      return 'Digital Delivery';
    case 'liveSession':
      return 'Live Session';
    default:
      return 'Digital Delivery';
  }
};

// Get service icon based on delivery type
export const getServiceIcon = deliveryType => {
  switch (deliveryType) {
    case 'physicalItem':
      return '📦';
    case 'digitalDelivery':
      return '⚡';
    case 'liveSession':
      return '🎥';
    default:
      return '🎵';
  }
};
